import type { FileContentResponse, FsEntry } from '@omni/shared';
import { isApiError } from '../api/client';
import { IconDownload, IconFile, IconFolder } from '../components/icons';
import { Button, Card, EmptyState, ErrorText, Spinner } from '../components/ui';
import { zh } from '../i18n';
import { basename, dirname, formatBytes, formatRelative } from '../lib/format';

/**
 * Tree + file viewer shared by 記憶 (Tier B) and 共享空間 (Tier C).
 *
 * Both tiers speak the same `FsEntry` shape (CONTRACTS §4.5/§4.6) and both are
 * read-only in v1, so one component serves both; the caller supplies the data
 * source and the download URL builder, which is also what keeps the two
 * authorization paths separate at the call site.
 */

export interface FileBrowserProps {
  readonly path: string;
  readonly onPathChange: (path: string) => void;
  readonly entries: readonly FsEntry[] | undefined;
  readonly treeLoading: boolean;
  readonly treeError: unknown;
  readonly selected: string | null;
  readonly onSelect: (path: string | null) => void;
  readonly file: FileContentResponse | undefined;
  readonly fileLoading: boolean;
  readonly fileError: unknown;
  readonly buildDownloadUrl: (path: string) => string;
  readonly rootLabel: string;
  readonly emptyLabel: string;
}

function crumbs(path: string, rootLabel: string): { label: string; path: string }[] {
  const out = [{ label: rootLabel, path: '' }];
  if (!path) return out;
  const parts = path.split('/').filter(Boolean);
  let acc = '';
  for (const part of parts) {
    acc = acc ? `${acc}/${part}` : part;
    out.push({ label: part, path: acc });
  }
  return out;
}

export function FileBrowser({
  path,
  onPathChange,
  entries,
  treeLoading,
  treeError,
  selected,
  onSelect,
  file,
  fileLoading,
  fileError,
  buildDownloadUrl,
  rootLabel,
  emptyLabel,
}: FileBrowserProps): JSX.Element {
  if (selected !== null) {
    return (
      <div className="stack">
        <div className="row">
          <Button
            variant="ghost"
            small
            onClick={() => {
              onSelect(null);
              onPathChange(dirname(selected));
            }}
          >
            {zh.app.back}
          </Button>
          <span className="small">{basename(selected)}</span>
          <span className="spacer" />
          <a className="btn btn--ghost btn--sm" href={buildDownloadUrl(selected)}>
            <IconDownload size={16} /> {zh.app.download}
          </a>
        </div>

        {fileLoading ? <Spinner /> : null}
        {isApiError(fileError, 'NOT_TEXT') ? (
          <div className="banner banner--warn">
            <span className="banner__text">{zh.memory.notText}</span>
          </div>
        ) : (
          <ErrorText error={fileError} />
        )}

        {file ? (
          <div className="file-view">
            {file.truncated ? (
              <div className="banner banner--info">
                <span className="banner__text">{zh.memory.truncated}</span>
              </div>
            ) : null}
            <pre className="file-view__pre">{file.content || zh.memory.fileEmpty}</pre>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="stack">
      <nav className="crumbs" aria-label={rootLabel}>
        {crumbs(path, rootLabel).map((crumb, index, all) => (
          <span key={crumb.path || 'root'}>
            <button type="button" onClick={() => onPathChange(crumb.path)}>
              {crumb.label}
            </button>
            {index < all.length - 1 ? <span aria-hidden="true"> / </span> : null}
          </span>
        ))}
      </nav>

      {treeLoading ? <Spinner /> : null}
      <ErrorText error={treeError} />

      {entries && entries.length === 0 ? <EmptyState title={emptyLabel} /> : null}

      {entries && entries.length > 0 ? (
        <Card flush>
          <ul className="list">
            {entries.map((entry) => (
              <li key={entry.path}>
                <button
                  type="button"
                  className="list__row"
                  onClick={() => {
                    if (entry.type === 'dir') onPathChange(entry.path);
                    else onSelect(entry.path);
                  }}
                >
                  {entry.type === 'dir' ? (
                    <IconFolder size={18} className="tool__icon" />
                  ) : (
                    <IconFile size={18} className="tool__icon" />
                  )}
                  <div className="list__main">
                    <div className="list__title">{entry.name}</div>
                    <div className="list__sub">
                      {entry.type === 'file' ? formatBytes(entry.size) : ''}
                      {entry.mtime ? ` · ${formatRelative(entry.mtime)}` : ''}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}
